import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  Briefcase,
  Linkedin,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertCircle,
  User as UserIcon,
  Building2,
  Clock,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ContactFormDialog } from "@/components/contact-form-dialog";

type EnrichField = "email" | "phone" | "jobTitle" | "linkedinUrl" | "all";

export function ContactDetailPage() {
  const { id } = useParams();
  const utils = trpc.useUtils();
  const [editOpen, setEditOpen] = useState(false);

  const contact = trpc.contacts.get.useQuery({ id: id! }, { enabled: !!id });

  const runs = trpc.contacts.listEnrichmentRuns.useQuery(
    { contactId: id!, limit: 10 },
    {
      enabled: !!id,
      refetchInterval: (q) => {
        const data = q.state.data;
        if (!data) return false;
        return data.some((r) => r.status === "pending" || r.status === "running") ? 2000 : false;
      },
    }
  );

  const providers = trpc.contacts.availableEnrichmentProviders.useQuery();

  const runChain = trpc.contacts.runEnrichmentChain.useMutation({
    onSuccess: () => {
      toast.success("Paleistas duomenų papildymas");
      utils.contacts.get.invalidate({ id: id! });
      utils.contacts.listEnrichmentRuns.invalidate({ contactId: id! });
    },
    onError: (err) => toast.error(err.message),
  });

  if (contact.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (contact.isError || !contact.data) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">Nepavyko įkrauti kontakto.</p>
      </div>
    );
  }

  const c = contact.data;
  const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || "—";
  const isAnyRunning = (runs.data ?? []).some(
    (r) => r.status === "pending" || r.status === "running"
  );

  const trigger = (field: EnrichField) => {
    runChain.mutate({ contactId: c.id, field });
  };

  return (
    <div className="mx-auto max-w-screen-lg space-y-4 px-4 py-6 md:px-6">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/contacts">
            <ArrowLeft className="h-4 w-4" />
            Kontaktai
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3 w-3" />
            Redaguoti
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={runChain.isPending || isAnyRunning}
            onClick={() => trigger("all")}
          >
            <Sparkles className={cn("h-3 w-3", (runChain.isPending || isAnyRunning) && "animate-pulse")} />
            Papildyti viską
          </Button>
        </div>
      </div>

      <ContactFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        clientId={c.clientId ?? null}
        initial={{
          id: c.id,
          firstName: c.firstName ?? "",
          lastName: c.lastName ?? "",
          email: c.email ?? "",
          phone: c.phone ?? "",
          mobilePhone: c.mobilePhone ?? "",
          jobTitle: c.jobTitle ?? "",
          linkedinUrl: c.linkedinUrl ?? "",
          notes: c.notes ?? "",
        }}
      />

      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-400 text-white">
          <UserIcon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{fullName}</h1>
          {c.clientName && c.clientId && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              <Link
                to={`/clients/${c.clientId}`}
                className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
              >
                <Building2 className="h-3.5 w-3.5" />
                {c.clientName}
              </Link>
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Duomenys
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <EnrichableField
                icon={<Briefcase className="h-4 w-4 shrink-0 text-muted-foreground" />}
                label="Pareigos"
                value={c.jobTitle}
                onEnrich={() => trigger("jobTitle")}
                disabled={runChain.isPending || isAnyRunning}
              />
              <EnrichableField
                icon={<Mail className="h-4 w-4 shrink-0 text-muted-foreground" />}
                label="El. paštas"
                value={c.email}
                href={c.email ? `mailto:${c.email}` : undefined}
                onEnrich={() => trigger("email")}
                disabled={runChain.isPending || isAnyRunning}
              />
              <EnrichableField
                icon={<Phone className="h-4 w-4 shrink-0 text-muted-foreground" />}
                label="Telefonas"
                value={c.phone ?? c.mobilePhone}
                href={c.phone || c.mobilePhone ? `tel:${c.phone ?? c.mobilePhone}` : undefined}
                onEnrich={() => trigger("phone")}
                disabled={runChain.isPending || isAnyRunning}
              />
              <EnrichableField
                icon={<Linkedin className="h-4 w-4 shrink-0 text-muted-foreground" />}
                label="LinkedIn"
                value={c.linkedinUrl}
                href={c.linkedinUrl ?? undefined}
                onEnrich={() => trigger("linkedinUrl")}
                disabled={runChain.isPending || isAnyRunning}
              />
            </CardContent>
          </Card>

          {c.notes && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  Pastabos
                </CardTitle>
              </CardHeader>
              <CardContent className="whitespace-pre-wrap text-sm">{c.notes}</CardContent>
            </Card>
          )}
        </div>

        <aside className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Integracijos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-xs">
              {(providers.data ?? []).map((p) => (
                <div key={p.key} className="flex items-center justify-between">
                  <span>{p.label}</span>
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-[10px] font-normal",
                      p.available ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"
                    )}
                  >
                    {p.available ? "aktyvi" : "neaktyvi"}
                  </Badge>
                </div>
              ))}
              {(providers.data ?? []).every((p) => !p.available) && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Sukonfigūruokite HUNTER_API_KEY, APOLLO_API_KEY arba ANTHROPIC_API_KEY .env faile.
                </p>
              )}
            </CardContent>
          </Card>

          <RunHistory runs={runs.data ?? []} loading={runs.isLoading} />
        </aside>
      </div>
    </div>
  );
}

function EnrichableField({
  icon,
  label,
  value,
  href,
  onEnrich,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  href?: string;
  onEnrich: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="group flex items-center gap-2 rounded-md border bg-background/60 px-3 py-2">
      {icon}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        {value ? (
          href ? (
            <a
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel={href.startsWith("http") ? "noreferrer" : undefined}
              className="block truncate text-sm hover:underline"
            >
              {value}
            </a>
          ) : (
            <div className="truncate text-sm">{value}</div>
          )
        ) : (
          <div className="text-sm text-muted-foreground/60">—</div>
        )}
      </div>
      <button
        type="button"
        onClick={onEnrich}
        disabled={disabled}
        title={`Papildyti: ${label}`}
        className={cn(
          "rounded-md p-1.5 text-muted-foreground transition",
          "opacity-60 group-hover:opacity-100 focus:opacity-100 md:opacity-0",
          "hover:bg-muted hover:text-foreground",
          "disabled:cursor-not-allowed disabled:opacity-30"
        )}
      >
        <Sparkles className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

type RunWithSteps = NonNullable<
  ReturnType<typeof trpc.contacts.listEnrichmentRuns.useQuery>["data"]
>[number];

function RunHistory({ runs, loading }: { runs: RunWithSteps[]; loading: boolean }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Papildymo istorija
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : runs.length === 0 ? (
          <p className="text-xs text-muted-foreground">Dar nepaleista.</p>
        ) : (
          runs.map((r) => <RunRow key={r.id} run={r} />)
        )}
      </CardContent>
    </Card>
  );
}

function RunRow({ run }: { run: RunWithSteps }) {
  const [open, setOpen] = useState(false);
  const status = run.status;
  const Icon =
    status === "succeeded"
      ? CheckCircle2
      : status === "failed"
        ? AlertCircle
        : status === "running" || status === "pending"
          ? Loader2
          : Clock;
  const tone =
    status === "succeeded"
      ? "text-emerald-700"
      : status === "failed"
        ? "text-rose-700"
        : "text-blue-700";

  return (
    <div className="rounded-md border bg-background/40 px-2.5 py-2 text-xs">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="flex items-center gap-1.5">
          <Icon
            className={cn(
              "h-3 w-3",
              tone,
              (status === "running" || status === "pending") && "animate-spin"
            )}
          />
          <span className="font-medium">{labelForField(run.field)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">{run.providers.join(" → ")}</span>
        </span>
        <span className="shrink-0 text-muted-foreground">
          {new Date(run.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </button>
      {open && (
        <div className="mt-2 space-y-1 border-t pt-2">
          {run.steps.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium">{s.provider}</div>
                {s.result?.notes && (
                  <div className="text-muted-foreground">{s.result.notes}</div>
                )}
                {s.error && <div className="text-rose-700">err: {s.error}</div>}
              </div>
              <Badge
                variant="secondary"
                className={cn(
                  "shrink-0 text-[10px] font-normal",
                  s.status === "succeeded" && "bg-emerald-100 text-emerald-700",
                  s.status === "failed" && "bg-rose-100 text-rose-700",
                  (s.status === "running" || s.status === "pending") && "bg-blue-100 text-blue-700"
                )}
              >
                {s.status}
              </Badge>
            </div>
          ))}
          {run.appliedPatch && Object.keys(run.appliedPatch).length > 0 && (
            <div className="mt-1 rounded bg-emerald-50 px-2 py-1 text-emerald-800">
              Įrašyta: {Object.keys(run.appliedPatch).join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function labelForField(field: string): string {
  switch (field) {
    case "email":
      return "El. paštas";
    case "phone":
      return "Telefonas";
    case "jobTitle":
      return "Pareigos";
    case "linkedinUrl":
      return "LinkedIn";
    case "all":
      return "Visi laukai";
    default:
      return field;
  }
}
